import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Badge, MapPinIcon } from '@eventbrite/marmalade';
import { defineMessages, useIntl } from 'react-intl';
import type { OrganizerLocation } from '@/types/organizer';
import styles from './LocationBadges.module.scss';

const badgeSurfaceStyle = {
  backgroundColor: '#F8F7FA',
  color: 'var(--color-neutral-600, #716b7a)',
  borderColor: 'transparent',
} as const;
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const overflowAriaMessages = defineMessages({
  showOneMoreLocation: {
    id: 'Show one more location',
    defaultMessage: 'Show one more location',
  },
  showMoreLocations: {
    id: 'Show {hiddenCount} more locations',
    defaultMessage: 'Show {hiddenCount} more locations',
  },
});

interface Props {
  locations: OrganizerLocation[];
}

function locationLabel(loc: OrganizerLocation): string {
  const d = loc.description.trim();
  if (d) return d;
  return loc.placeId.trim();
}

export function googleMapsPlaceUrl(placeId: string): string {
  const id = placeId.trim();
  if (!id) return '';
  const q = `place_id:${id}`;
  return `https://www.google.com/maps/place/?q=${encodeURIComponent(q)}`;
}

function LocationBadge({ loc }: { loc: OrganizerLocation }) {
  const label = locationLabel(loc);
  return (
    <Badge
      variant="custom"
      bordered={false}
      className={styles.badge}
      icon={<MapPinIcon size="xsmall" aria-hidden />}
      style={badgeSurfaceStyle}
    >
      {label}
    </Badge>
  );
}

function LocationBadgeLink({ loc }: { loc: OrganizerLocation }) {
  const intl = useIntl();
  const label = locationLabel(loc);
  const href = googleMapsPlaceUrl(loc.placeId);
  const badge = <LocationBadge loc={loc} />;

  if (!href) {
    return <span className={styles.badgeWrap}>{badge}</span>;
  }

  return (
    <a
      className={styles.badgeWrap}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={intl.formatMessage(
        {
          id: 'Open {locationLabel} in Google Maps',
          defaultMessage: 'Open {locationLabel} in Google Maps',
        },
        { locationLabel: label },
      )}
    >
      {badge}
    </a>
  );
}

export function LocationBadges({ locations }: Props) {
  const intl = useIntl();
  const locationRows = useMemo(
    () => locations.filter((loc) => Boolean(locationLabel(loc))),
    [locations],
  );
  const normalized = locationRows.map(locationLabel);
  const n = normalized.length;
  const layoutSignature = useMemo(
    () => locationRows.map((l) => `${l.placeId}\u0001${l.description}`).join('|'),
    [locationRows],
  );

  const listRef = useRef<HTMLUListElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = useState(n);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [layoutSignature]);

  useIsomorphicLayoutEffect(() => {
    if (n === 0) {
      return;
    }

    if (expanded) {
      setVisibleCount(n);
      return;
    }

    const listEl = listRef.current;
    const measureEl = measureRef.current;
    if (!listEl || !measureEl) {
      return;
    }

    setVisibleCount(n);

    const measureRow = measureEl.firstElementChild as HTMLElement | null;

    const runLayout = () => {
      const containerWidth = listEl.clientWidth;
      const gapPx = readGapPx(measureRow);

      const locNodes = measureRow?.querySelectorAll<HTMLElement>('[data-measure="location"]') ?? [];
      const locationWidths = Array.from(locNodes).map((node) => node.getBoundingClientRect().width);

      if (locationWidths.length !== n) {
        return;
      }

      const plusNodes =
        measureRow?.querySelectorAll<HTMLElement>('[data-measure="overflow"]') ?? [];
      const plusWidthByHidden = new Map<number, number>();
      plusNodes.forEach((node) => {
        const hidden = Number(node.dataset.hiddenCount);
        if (Number.isFinite(hidden) && hidden >= 1 && hidden <= n) {
          plusWidthByHidden.set(hidden, node.getBoundingClientRect().width);
        }
      });

      const overflowBadgeWidth = (hidden: number) =>
        plusWidthByHidden.get(hidden) ?? plusWidthByHidden.get(1) ?? 32;

      const show = computeVisibleLocationCount(
        containerWidth,
        locationWidths,
        gapPx,
        overflowBadgeWidth,
      );

      setVisibleCount(show);
    };

    runLayout();

    const ro = new ResizeObserver(() => {
      runLayout();
    });
    ro.observe(listEl);

    return () => {
      ro.disconnect();
    };
  }, [n, layoutSignature, expanded]);

  if (n === 0) {
    return null;
  }

  const hiddenCount = n - visibleCount;
  const visibleRows = locationRows.slice(0, visibleCount);

  return (
    <div className={styles.wrapper}>
      <div ref={measureRef} className={styles.measureRoot} aria-hidden>
        <div className={styles.measureRow}>
          {locationRows.map((loc, index) => (
            <div
              key={`m-loc-${loc.placeId || index}`}
              className={styles.measureChip}
              data-measure="location"
            >
              <LocationBadge loc={loc} />
            </div>
          ))}
          {Array.from({ length: n }, (_, i) => i + 1).map((hidden) => (
            <div
              key={`m-plus-${hidden}`}
              className={styles.measureChip}
              data-measure="overflow"
              data-hidden-count={String(hidden)}
            >
              <Badge
                variant="custom"
                bordered={false}
                className={styles.badge}
                style={badgeSurfaceStyle}
              >
                +{hidden}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      <ul
        ref={listRef}
        className={expanded ? `${styles.list} ${styles.listExpanded}` : styles.list}
        aria-label={intl.formatMessage({
          id: 'Organizer locations',
          defaultMessage: 'Organizer locations',
        })}
      >
        {visibleRows.map((loc, index) => (
          <li key={loc.placeId || `loc-${index}`} className={styles.item}>
            <LocationBadgeLink loc={loc} />
          </li>
        ))}
        {hiddenCount > 0 && (
          <li className={styles.item}>
            <button
              type="button"
              className={styles.overflowButton}
              onClick={() => setExpanded(true)}
              aria-label={intl.formatMessage(
                hiddenCount === 1
                  ? overflowAriaMessages.showOneMoreLocation
                  : overflowAriaMessages.showMoreLocations,
                hiddenCount === 1 ? undefined : { hiddenCount },
              )}
            >
              <Badge
                variant="custom"
                bordered={false}
                className={styles.badge}
                style={badgeSurfaceStyle}
              >
                +{hiddenCount}
              </Badge>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

function readGapPx(row: HTMLElement | null): number {
  if (!row) {
    return 8;
  }
  const raw = getComputedStyle(row).columnGap || getComputedStyle(row).gap;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 8;
}

/**
 * How many location chips fit in one row next to an optional "+hiddenCount" overflow badge.
 */
export function computeVisibleLocationCount(
  containerWidth: number,
  locationWidths: readonly number[],
  gapPx: number,
  overflowBadgeWidth: (hiddenCount: number) => number,
): number {
  const n = locationWidths.length;
  if (n === 0) {
    return 0;
  }

  const safeW = Math.max(0, containerWidth);

  for (let show = n; show >= 0; show -= 1) {
    const hidden = n - show;

    if (hidden === 0) {
      let total = 0;
      for (let i = 0; i < show; i += 1) {
        total += locationWidths[i] ?? 0;
        if (i > 0) {
          total += gapPx;
        }
      }
      if (total <= safeW) {
        return show;
      }
      continue;
    }

    let total = 0;
    for (let i = 0; i < show; i += 1) {
      total += locationWidths[i] ?? 0;
      if (i > 0) {
        total += gapPx;
      }
    }
    if (show > 0) {
      total += gapPx;
    }
    total += overflowBadgeWidth(hidden);

    if (total <= safeW) {
      return show;
    }
  }

  return 0;
}
